import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { IconPlayerPlay, IconTemplate, IconHelp, IconX } from '@tabler/icons';
import { QUERY_PRESETS } from './queryPresets';

interface LogQueryToolbarProps {
  query: string;
  onChange: (query: string) => void;
  onSubmit: () => void;
  error?: string | null;
  isLoading: boolean;
}

const SYNTAX_HELP = `Supported Commands:
  filter <expression>              Filter log events
  sort <field> asc|desc            Sort results
  limit <N>                        Limit results (max 10,000)
  stats <agg> by <field>           Aggregate (count, avg, sum, min, max)
  parse <field> "pattern" as <name>  Extract fields

Filter Operators:
  =  !=  <  >  <=  >=             Comparison
  like  not like                   Regex match (/pattern/)
  in  not in                       Set membership (["a", "b"])
  and  or  not  ( )                Boolean logic

Fields:
  @timestamp  @message  level  job_id  flow_job_id
  integration_id  exc_info  @logStream  message

Examples:
  filter level = "error" | sort @timestamp desc
  filter level in ["error", "warn"] and @message like /timeout/
  stats count(*) by level`;

const LogQueryToolbar: React.FC<LogQueryToolbarProps> = ({
  query,
  onChange,
  onSubmit,
  error,
  isLoading,
}) => {
  const [presetsAnchor, setPresetsAnchor] = React.useState<null | HTMLElement>(
    null,
  );
  const [helpAnchor, setHelpAnchor] = React.useState<null | HTMLElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      <TextField
        value={query}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder='filter level = "error" | sort @timestamp desc'
        fullWidth
        size="small"
        InputProps={{
          sx: {
            fontFamily: 'monospace',
            fontSize: '13px',
          },
          endAdornment: (
            <InputAdornment position="end">
              {query && (
                <IconButton onClick={() => onChange('')} size="small">
                  <IconX size={16} />
                </IconButton>
              )}
              <IconButton onClick={onSubmit} disabled={isLoading} size="small">
                {isLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <IconPlayerPlay size={18} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
        error={Boolean(error)}
        helperText={error}
      />

      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
        <Button
          size="small"
          onClick={(e) => setPresetsAnchor(e.currentTarget)}
          startIcon={<IconTemplate size={16} />}
          sx={{ textTransform: 'none' }}
        >
          Presets
        </Button>
        <Menu
          anchorEl={presetsAnchor}
          open={Boolean(presetsAnchor)}
          onClose={() => setPresetsAnchor(null)}
        >
          {QUERY_PRESETS.map((preset) => (
            <MenuItem
              key={preset.label}
              onClick={() => {
                onChange(preset.query);
                setPresetsAnchor(null);
              }}
            >
              <ListItemText
                primary={preset.label}
                secondary={
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace', fontSize: '11px' }}
                  >
                    {preset.query}
                  </Typography>
                }
              />
            </MenuItem>
          ))}
        </Menu>

        <Button
          size="small"
          onClick={(e) => setHelpAnchor(e.currentTarget)}
          startIcon={<IconHelp size={16} />}
          sx={{ textTransform: 'none' }}
        >
          Query Help
        </Button>
        <Popover
          open={Boolean(helpAnchor)}
          anchorEl={helpAnchor}
          onClose={() => setHelpAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 2, maxWidth: 480 }}>
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                m: 0,
              }}
            >
              {SYNTAX_HELP}
            </Typography>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default LogQueryToolbar;
